/**
 * API · core.task_labels + core.task_label_links
 *
 * Modelo: task_labels es la lista de labels disponibles (slug, color),
 * task_label_links es la m2m con tasks. Para mostrar #hashtags en una card
 * batcheamos con un solo IN (...) sobre task_id.
 */
import { supabase } from '@/lib/supabase';
import type { TaskLabelsRow } from '@/types/database.types';

export interface LabelLinkWithLabel {
  task_id: string;
  label: Pick<TaskLabelsRow, 'id' | 'name' | 'slug' | 'color'>;
}

/**
 * Devuelve los labels vinculados a un conjunto de tareas, agrupables en el
 * cliente. Una sola query con JOIN al label, filtrada por task_id IN (...).
 */
export async function listLabelsForTasks(
  taskIds: string[],
): Promise<LabelLinkWithLabel[]> {
  if (taskIds.length === 0) return [];
  const { data, error } = await supabase
    .from('task_label_links')
    .select('task_id, label:task_labels(id, name, slug, color)')
    .in('task_id', taskIds);
  if (error) throw error;
  // El select devuelve label como objeto único (FK 1:1 desde el link).
  return (data ?? []).map((row) => ({
    task_id: (row as unknown as { task_id: string }).task_id,
    label: (row as unknown as { label: LabelLinkWithLabel['label'] }).label,
  }));
}

/** Lista global de labels disponibles para un área (autocomplete en formularios). */
export async function listAvailableLabels(areaId?: string): Promise<TaskLabelsRow[]> {
  let q = supabase.from('task_labels').select('*').order('name', { ascending: true });
  if (areaId) q = q.or(`area_id.eq.${areaId},area_id.is.null`);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

/**
 * Resuelve un array de label-slugs:
 *  - Busca cada slug en core.task_labels.
 *  - Si NO existe, lo inserta con name = slug (capitalizable después por el
 *    user desde admin de labels).
 *  - Devuelve el array de IDs en el mismo orden que los slugs.
 *
 * Usado en el flow de "crear tarea con tags": el user tipea #urgente #bug y
 * el form llama acá para garantizar que esos labels existan antes de
 * insertar los task_label_links.
 */
export async function ensureLabelsBySlug(
  slugs: string[],
  areaId: string | null,
  createdBy: string,
): Promise<string[]> {
  if (slugs.length === 0) return [];

  const cleanSlugs = [...new Set(slugs.map((s) => s.trim().toLowerCase()).filter(Boolean))];
  if (cleanSlugs.length === 0) return [];

  // Buscar los que ya existen
  const { data: existing, error: selErr } = await supabase
    .from('task_labels')
    .select('id, slug')
    .in('slug', cleanSlugs);
  if (selErr) throw selErr;

  const existingMap = new Map((existing ?? []).map((r) => [r.slug, r.id]));
  const missing = cleanSlugs.filter((s) => !existingMap.has(s));

  if (missing.length > 0) {
    const toInsert = missing.map((slug) => ({
      slug,
      name: slug, // el name lo puede editar el admin después
      area_id: areaId,
      created_by: createdBy,
    }));
    const { data: inserted, error: insErr } = await supabase
      .from('task_labels')
      .insert(toInsert)
      .select('id, slug');
    if (insErr) throw insErr;
    for (const r of inserted ?? []) existingMap.set(r.slug, r.id);
  }

  return cleanSlugs.map((s) => existingMap.get(s)!).filter(Boolean);
}

/** Batch insert de relaciones task ↔ label. Idempotente por (task_id, label_id). */
export async function linkLabelsToTask(taskId: string, labelIds: string[]): Promise<void> {
  if (labelIds.length === 0) return;
  const rows = labelIds.map((label_id) => ({ task_id: taskId, label_id }));
  // upsert ignorar duplicados — la PK natural es (task_id, label_id)
  const { error } = await supabase
    .from('task_label_links')
    .upsert(rows, { onConflict: 'task_id,label_id', ignoreDuplicates: true });
  if (error) throw error;
}
